import { auth } from '../firebase';

const API_BASE_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api`;

// Backend response interfaces
export interface FavoriteUrl {
  url: string;
  order: number;
}

interface DeviceConfig {
  volume: number;
  blocked_urls: string[];
  favorite_urls: FavoriteUrl[];
}

interface DeviceStatus {
  // Stays 'bike'/'sleep' after bike_time/sleep_time pass — compare those timestamps to now
  status: 'offline' | 'bike' | 'sleep';
  current_url?: string;
  bike_time?: number;
  sleep_time?: number;
  last_seen?: number;
  last_ban_time?: number;
}

interface BackendDevice {
  id: string;
  name: string;
  created_at: number;
  updated_at: number;
  config: DeviceConfig;
  status: DeviceStatus;
  schedules: {
    bike?: { start: string; end: string; }[];
    sleep?: { start: string; end: string; }[];
  };
}

interface DeviceACL {
  owners: Record<string, boolean>;
  members: Record<string, { role: 'viewer' | 'editor'; granted_at: number; granted_by: string; }>;
}

interface DeviceResponse {
  device: BackendDevice;
  acl: DeviceACL;
  my_role: 'owner' | 'viewer' | 'editor';
}

/** A non-2xx answer from the backend, with its HTTP status (409: a list changed meanwhile). */
export class ApiError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
    this.name = 'ApiError';
  }
}

class ApiService {
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const user = auth.currentUser;

    if (!user) {
      console.error('[API] No current user found');
      throw new Error('User not authenticated');
    }

    const token = await user.getIdToken();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers = await this.getAuthHeaders();
    const url = `${API_BASE_URL}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });


    if (!response.ok) {
      // Error bodies are not always JSON (e.g. an HTML 502 from Cloud Run or a proxy).
      const error = await response.json().catch(() => ({}));
      console.error('[API] Request failed:', response.status, error);
      throw new ApiError(error.detail || `Request failed (${response.status})`, response.status);
    }

    return response.json();
  }

  // Device management
  async getDevices() {
    const response = await this.request<{ devices: DeviceResponse[] }>('/devices/');
    // Transform the nested backend response to the flat frontend structure
    const transformedDevices = response.devices.map(deviceResponse => ({
      id: deviceResponse.device.id,
      name: deviceResponse.device.name,
      volume: deviceResponse.device.config.volume,
      blocked_urls: deviceResponse.device.config.blocked_urls,
      favorite_urls: deviceResponse.device.config.favorite_urls || [],
      current_url: deviceResponse.device.status.current_url,
      bike_time: deviceResponse.device.status.bike_time,
      sleep_time: deviceResponse.device.status.sleep_time,
      status: deviceResponse.device.status.status,
      last_seen: deviceResponse.device.status.last_seen,
      last_ban_time: deviceResponse.device.status.last_ban_time,
      created_at: deviceResponse.device.created_at,
      updated_at: deviceResponse.device.updated_at,
      schedules: deviceResponse.device.schedules,
      owner_uid: Object.keys(deviceResponse.acl.owners)[0] || '',
      is_owner: deviceResponse.my_role === 'owner',
      role: deviceResponse.my_role
    }));

    return { devices: transformedDevices };
  }

  async createDevice(name: string) {
    const response = await this.request<DeviceResponse>('/devices/', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });

    // Transform the nested backend response to the flat frontend structure
    return {
      id: response.device.id,
      name: response.device.name,
      volume: response.device.config.volume,
      blocked_urls: response.device.config.blocked_urls,
      favorite_urls: response.device.config.favorite_urls || [],
      current_url: response.device.status.current_url,
      bike_time: response.device.status.bike_time,
      sleep_time: response.device.status.sleep_time,
      status: response.device.status.status,
      last_seen: response.device.status.last_seen,
      last_ban_time: response.device.status.last_ban_time,
      created_at: response.device.created_at,
      updated_at: response.device.updated_at,
      schedules: response.device.schedules,
      owner_uid: Object.keys(response.acl.owners)[0] || '',
      is_owner: response.my_role === 'owner',
      role: response.my_role
    };
  }

  async updateDevice(deviceId: string, updates: Partial<Device> & { ban_current_url?: boolean; ban_current_domain?: boolean; favorite_current_url?: boolean }) {
    // Transform frontend updates to backend format
    const backendUpdates: any = {};

    if (updates.volume !== undefined) backendUpdates.volume = updates.volume;
    if (updates.blocked_urls !== undefined) backendUpdates.blocked_urls = updates.blocked_urls;
    if (updates.current_url !== undefined) backendUpdates.current_url = updates.current_url;
    if (updates.status === 'bike' && updates.bike_time) {
      const minutesFromNow = Math.ceil((updates.bike_time - Date.now()) / (1000 * 60));
      backendUpdates.bike_minutes = minutesFromNow > 0 ? minutesFromNow : 1;
    }
    if (updates.status === 'sleep' && updates.sleep_time) {
      const minutesFromNow = Math.ceil((updates.sleep_time - Date.now()) / (1000 * 60));
      backendUpdates.sleep_minutes = minutesFromNow > 0 ? minutesFromNow : 1;
    }
    if (updates.ban_current_url !== undefined) backendUpdates.ban_current_url = updates.ban_current_url;
    if (updates.ban_current_domain !== undefined) backendUpdates.ban_current_domain = updates.ban_current_domain;
    if (updates.favorite_current_url !== undefined) backendUpdates.favorite_current_url = updates.favorite_current_url;

    const response = await this.request<DeviceResponse>(`/devices/${deviceId}`, {
      method: 'PUT',
      body: JSON.stringify(backendUpdates),
    });

    // Transform the nested backend response to the flat frontend structure
    return {
      id: response.device.id,
      name: response.device.name,
      volume: response.device.config.volume,
      blocked_urls: response.device.config.blocked_urls,
      favorite_urls: response.device.config.favorite_urls || [],
      current_url: response.device.status.current_url,
      bike_time: response.device.status.bike_time,
      sleep_time: response.device.status.sleep_time,
      status: response.device.status.status,
      last_seen: response.device.status.last_seen,
      last_ban_time: response.device.status.last_ban_time,
      created_at: response.device.created_at,
      updated_at: response.device.updated_at,
      schedules: response.device.schedules,
      owner_uid: Object.keys(response.acl.owners)[0] || '',
      is_owner: response.my_role === 'owner',
      role: response.my_role
    };
  }

  async deleteDevice(deviceId: string) {
    return this.request<{ message: string }>(`/devices/${deviceId}`, {
      method: 'DELETE',
    });
  }

  async getDeviceConfig(deviceId: string) {
    return this.request<{ volume: number; blocked_urls: string[] }>(
      `/devices/${deviceId}/config`
    );
  }

  async updateDeviceConfig(deviceId: string, config: { volume?: number; blocked_urls?: string[] }) {
    return this.request<{ volume: number; blocked_urls: string[] }>(
      `/devices/${deviceId}/config`,
      {
        method: 'PUT',
        body: JSON.stringify(config),
      }
    );
  }

  async getBlockedUrls(deviceId: string) {
    return this.request<{ blocked_urls: string[] }>(
      `/devices/${deviceId}/config/blocked_urls`
    );
  }

  async updateBlockedUrls(deviceId: string, blockedUrls: string[]) {
    return this.request<{ blocked_urls: string[] }>(
      `/devices/${deviceId}/config/blocked_urls`,
      {
        method: 'PUT',
        body: JSON.stringify({ blocked_urls: blockedUrls }),
      }
    );
  }

  async getFavoriteUrls(deviceId: string) {
    return this.request<{ favorite_urls: FavoriteUrl[] }>(
      `/devices/${deviceId}/config/favorite_urls`
    );
  }

  async updateFavoriteUrls(deviceId: string, favoriteUrls: FavoriteUrl[]) {
    return this.request<{ favorite_urls: FavoriteUrl[] }>(
      `/devices/${deviceId}/config/favorite_urls`,
      {
        method: 'PUT',
        body: JSON.stringify({ favorite_urls: favoriteUrls }),
      }
    );
  }

  // Invitation management
  async inviteToDevice(deviceId: string, email: string, role: 'viewer' | 'editor' = 'editor') {
    return this.request<{ message: string; invite_id: string; token: string }>(`/invites/devices/${deviceId}`, {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    });
  }

  async acceptInvite(token: string) {
    return this.request<{ message: string; device_id: string; role: string }>('/invites/accept', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async getPendingInvites() {
    return this.request<{ pending_invites: PendingInvite[] }>('/invites/pending');
  }

  async revokeInvite(inviteId: string) {
    return this.request<{ message: string }>(`/invites/${inviteId}`, {
      method: 'DELETE',
    });
  }

  // Health check
  async checkStatus() {
    return this.request<{ status: string; version: string; features: string[] }>('/status');
  }
}

// Frontend interface
export interface Device {
  id: string;
  name: string;
  volume: number;
  blocked_urls: string[];
  favorite_urls: FavoriteUrl[];
  current_url?: string;
  bike_time?: number;
  sleep_time?: number;
  last_ban_time?: number;
  schedules?: {
    bike?: { start: string; end: string; }[];
    sleep?: { start: string; end: string; }[];
  };
  status: 'offline' | 'bike' | 'sleep';
  last_seen?: number;
  created_at: number;
  updated_at: number;
  owner_uid: string;
  is_owner: boolean;
  role?: 'owner' | 'viewer' | 'editor';
}

export interface PendingInvite {
  invite_id: string;
  device_id: string;
  device_name: string;
  role: 'viewer' | 'editor';
  invited_by: string;
  created_at: number;
  expires_at: number;
  token: string;
}

export const apiService = new ApiService();